import { loginExtension } from '@interhyp/api-account';
import { searchWidgetPlugin } from '@interhyp/api-portal';
import { currentIntentValue } from '@interhyp/api-values';
import { useIdentity } from '@interhyp/platform-auth';
import { RootLayout } from '@interhyp/platform-layout';
import { Header, HeaderContext } from '@interhyp/platform-navigation';
import { PlatformRouter, RouteConfiguration } from '@interhyp/platform-router';
import { lazy, Suspense, useEffect, useMemo } from 'react';
import '@interhyp/platform-navigation/esm/index.css';
import { registerExtension, activateExtension } from '@interhyp/api-registration';
import { useDispatch } from 'react-redux';

import { ViewportFallback } from './Viewport.fallback';

import { devtoolsPlugin } from '@interhyp/api-devtools';

import { useCheckEmbedded } from '../hooks/useCheckEmbedded';
import { useCoreTracking } from '../hooks/useCoreTracking';
import { useEnvBridge } from '../hooks/useEnvBridge';
import { useIsEmbedded } from '../hooks/useIsEmbedded';
import { useLoadProfile } from '../hooks/useLoadProfile';
import { useRouteTracking } from '../hooks/useRouteTracking';
import { useRouterIntentId } from '../hooks/useRouterIntentId';
import { useSessionTracking } from '../hooks/useSessionTracking';
import { useWatchPermissions } from '../hooks/useWatchPermissions';
import { useWatchTranslations } from '../hooks/useWatchTranslations';
import { loadIntents } from '../redux/Intents/actions';

const SecureRoute = lazy(() => import('./secure/Secure.route'));
const EmployeeLoginRoute = lazy(() => import('./employeeLogin/EmployeeLogin.route'));

const AktivierungRoute = lazy(() => import('../routes/Aktivierung.route'));
const AktivierungBestaetigenRoute = lazy(() => import('../routes/AktivierungBestaetigen.route'));
const BrokerPreinquiryRoute = lazy(() => import('./brokerPreinquiry/BrokerPreinquiry.route'));
const BrokerEldrRequestRoute = lazy(() => import('./brokerEldrRequest/BrokerEldrRequest.route'));
const RegexRedirect = lazy(() => import('../components/RegexRedirect/RegexRedirect'));

const LoginRoute = lazy(() => import('../routes/Login.route'));
const ActivateRoute = lazy(() => import('../routes/Activate.route'));
const RegisterRoute = lazy(() => import('../routes/Register.route'));

export function Viewport(): JSX.Element {
  useWatchTranslations();
  useWatchPermissions();
  useLoadProfile();
  useEnvBridge();
  useCheckEmbedded();

  useCoreTracking();
  useSessionTracking();
  useRouteTracking();

  const activeIntentId = useRouterIntentId();
  const [_, setStoredIntentId] = currentIntentValue.useValue();
  const LoginRoutePresent = !!loginExtension.useExtension();
  const RegisterRoutePresent = !!registerExtension.useExtension();
  const ActivateRoutePresent = !!activateExtension.useExtension();
  const searchWidget = searchWidgetPlugin.useExtension();
  const userId = useIdentity()?.userId;
  const isEmbedded = useIsEmbedded();

  const routes = useMemo(() => {
    const internalRoutes: RouteConfiguration[] = [
      { path: '/home/api/business/auth/employee', component: EmployeeLoginRoute, exact: true },
      { path: '/home/api/business/auth/employee/deep', component: EmployeeLoginRoute, exact: true },
      { path: '/:id/finanzierung', component: EmployeeLoginRoute, exact: true },
      { path: '/:id/start', component: EmployeeLoginRoute, exact: true },
      {
        path: '/:id/voranfrage',
        component: BrokerPreinquiryRoute,
        exact: true,
      },
      {
        path: '/:id/voranfrage/:preinquiryId',
        component: BrokerPreinquiryRoute,
        exact: true,
      },
      {
        path: '/:id/vorhaben-neu/immobilie/:estateId/darlehen',
        component: BrokerEldrRequestRoute,
        exact: true,
      },
      { path: '/aktivierung/:token', component: AktivierungRoute, exact: true },
      {
        path: '/aktivierung-bestaetigen/:token',
        component: AktivierungBestaetigenRoute,
        exact: true,
      },
      {
        path: '/:intentId([0-9]+)',
        component: RegexRedirect,
      },
      { path: '*', component: SecureRoute },
    ];

    if (new URLSearchParams(location.search).get('client') === 'broker') {
      internalRoutes.unshift({
        path: '/login',
        component: EmployeeLoginRoute,
      });
    } else if (LoginRoutePresent) {
      internalRoutes.unshift({
        path: '/login',
        component: LoginRoute,
      });
    }

    if (RegisterRoutePresent) {
      internalRoutes.unshift({
        path: '/registrierung',
        component: RegisterRoute,
      });
    }

    if (ActivateRoutePresent) {
      internalRoutes.unshift({
        path: '/registration',
        component: ActivateRoute,
      });
    }

    return internalRoutes;
  }, [ActivateRoutePresent, LoginRoutePresent, RegisterRoutePresent]);

  const SearchWidget = searchWidget?.component;
  const DevTools = devtoolsPlugin.useExtension()?.component;
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(loadIntents(undefined, activeIntentId, userId));
  }, [activeIntentId, dispatch, userId]);

  // Update stored intent id depending on routing
  useEffect(() => {
    if (activeIntentId && userId) {
      setStoredIntentId({
        userId,
        intentId: activeIntentId,
      });
    }
  }, [activeIntentId, setStoredIntentId, userId]);

  useEffect(() => {
    const { body } = document;

    if (isEmbedded) {
      body.style.setProperty('--navigation-height', `0px`);

      return () => {
        body.style.removeProperty('--navigation-height');
      };
    }
  }, [isEmbedded]);

  return (
    <HeaderContext>
      <RootLayout>
        <Suspense fallback={null}>{DevTools && <DevTools />}</Suspense>
        {!isEmbedded && (
          // Hide navigation and all related stuff if portal is used in "embedded mode" (iframe monolith)
          <>
            <Header />
            <Suspense fallback={<div />}>{SearchWidget && <SearchWidget />}</Suspense>
          </>
        )}
        <main>
          <PlatformRouter routes={routes} fallback={ViewportFallback} secure />
        </main>
      </RootLayout>
    </HeaderContext>
  );
}
